import React from "react";
import { unstable_batchedUpdates as batch } from "react-dom";
import { useAsyncValidation } from "../hooks/useAsyncValidation";
import { useDebounce } from "../hooks/useDebounce";
import { useEvent } from "../hooks/useEvent";
import { ErrorLike } from "../PropX";
import * as RegionSelector from "../RegionSelector_v2";
import { AsyncData, asyncData, AsyncDataHelpers } from "../utils/AsyncData";
import { Maybe, maybe } from "../utils/Maybe";
import { MaybeEntityFormState, transformDataToMaybeEntityFormState } from "./EntityFormState";

export type Tag = {
  value: string;
  icon?: string;
  disabled?: boolean;
};

type StateTag = {
  value: string;
  group: string;
  icon?: string;
  disabled?: boolean;
};

export type SelectedTag = {
  value: string;
  icon?: string;
};

export type Project = {
  value: string;
  title: string;
  disabled: boolean;
  icon?: string;
};

type UseDataHookParameter<Extra> = {
  data: { tagSearch: string };
  extra: Extra;
  helpers: {
    async: AsyncDataHelpers;
    location: RegionSelector.Types.LocationHelpers;
  };
};

type AsyncDataWithOptions<T extends { value: string }> = AsyncData<{ options: T[]; defaultValue?: string | null }>;
type AsyncDataWithoutOptions = AsyncData<{ options: []; defaultValue?: null }>;

type UseDataHookResult = {
  resourceName?: { defaultValue: string };
  projects?: AsyncDataWithOptions<Project> | AsyncDataWithoutOptions;
  tags?: AsyncData<{ type: "suggested" | "matched"; options: Tag[]; searching: boolean }>;
  tagsDefaultValue?: SelectedTag[];
};

type UseDataHook<Extra> = (param: UseDataHookParameter<Extra>) => UseDataHookResult;

type HTMLInputNames = {
  resourceName: string;
  project: string;
  tags: string;
};

type Validation = {
  resourceName?: Partial<{
    minLength: number;
    maxLength: number;
    pattern: string;
    patternMismatchMessage: string;
    custom(value: string): Promise<ErrorLike | void>;
  }>;
};

type HookParameterWithoutExtra = {
  htmlInputNames?: Partial<HTMLInputNames>;
  /**
   * Additional custom validation to perform on the form. This is in addition to the validation performed by this hook already.
   */
  validation?: Validation;
};

type HookParameterWithExtra<Extra> = HookParameterWithoutExtra & { extra: Extra };

type ExtraStateData = {
  name: string;
  onSelect: (value: string) => void;
};

type ResourceNameState = {
  name: string;
  value: string;
  onChange: (value: string) => void;
  validating: boolean;
  minLength: number;
  maxLength: number;
  pattern: string;
  ref: React.RefObject<HTMLInputElement>;
  patternMismatchMessage?: string;
  error?: ErrorLike;
};

type TagsState = {
  name: string;
  search: string;
  selected: SelectedTag[];
  onChangeSearch(search: string): void;
  onSelectMany(selected: SelectedTag[]): void;
  data: AsyncData<{ options: StateTag[]; searching: boolean }>;
};

export type CreatePageFinalizeState = {
  projects: AsyncData<MaybeEntityFormState<Project, ExtraStateData>>;
  resourceName: Maybe<ResourceNameState>;
  tags: Maybe<TagsState>;
  valid: boolean;
  values: {
    resourceName: string;
    project: string | null;
    tags: string[];
  };
};

const useResourceName = () => {
  type State = {
    dirty: boolean;
    value: string;
  };

  type Action = { type: "SET_DEFAULT_VALUE"; value: string } | { type: "SET_VALUE"; value: string };

  const [state, dispatch] = React.useReducer(
    (prev: State, action: Action): State => {
      switch (action.type) {
        case "SET_DEFAULT_VALUE":
          if (prev.dirty || prev.value === action.value) {
            return prev;
          }

          return {
            dirty: false,
            value: action.value,
          };

        case "SET_VALUE":
          return {
            dirty: true,
            value: action.value,
          };
      }
    },
    { dirty: false, value: "" }
  );

  const setValue = useEvent((value: string) => {
    dispatch({ type: "SET_VALUE", value });
  });

  const setDefaultValue = useEvent((value: string) => {
    dispatch({ type: "SET_DEFAULT_VALUE", value });
  });

  return [state.value, setValue, setDefaultValue] as const;
};

const helpers = {
  async: asyncData,
  location: RegionSelector.locationHelpers,
};

const nothing = Object.freeze([]) as [];
const readyNothing = Object.freeze(helpers.async.ready({ options: nothing }));
const noopValidation = () => undefined;

/**
 * A factory function that creates a hook `useCreatePageFinalizeState` to be used in conjunction with `CreatePage.Finalize`.
 * It requires a hook as a parameter which is used to fetch data for the components.
 */

export function createCreatePageFinalizeState(useData: UseDataHook<void>): (param?: HookParameterWithoutExtra) => CreatePageFinalizeState;

export function createCreatePageFinalizeState<Extra>(
  useData: UseDataHook<Extra>
): (param: HookParameterWithExtra<Extra>) => CreatePageFinalizeState;

export function createCreatePageFinalizeState(useData: UseDataHook<any>) {
  return (param: HookParameterWithExtra<any> = { extra: undefined }): CreatePageFinalizeState => {
    const { htmlInputNames = {}, extra } = param;

    const {
      resourceName: {
        minLength: minResourceNameLength = 3,
        maxLength: maxResourceNameLength = 63,
        pattern: resourceNamePattern = "^[a-z0-9-]*$",
        patternMismatchMessage: resourcePatternMismatchMessage = "Name must only contain letters, numbers and dashes.",
        custom: customResourceNameValidation = noopValidation,
      } = {},
    } = param.validation ?? {};

    const [tagSearch, setTagSearch] = React.useState("");
    const [resourceName, setResourceName, setDefaultResourceName] = useResourceName();

    /**
     * Delay forwarding the search value to `useData` so that we can debounce API calls while the user is typing.
     */
    const debouncedTagSearch = useDebounce(tagSearch, tagSearch.length === 0 ? 0 : 200);
    const debouncedResourceName = useDebounce(resourceName, resourceName.length === 0 ? 0 : 200);

    const result = useData({
      data: { tagSearch: debouncedTagSearch },
      extra,
      helpers,
    });

    const [project, setProject] = React.useState<string | null>(null);
    const [selectedTags, setSelectedTags] = React.useState<SelectedTag[]>(() => {
      return result.tagsDefaultValue ?? [];
    });

    const resourceNameRef = React.useRef<HTMLInputElement>(null);

    const isResourceNameValid = useEvent(() => resourceNameRef.current?.validity.valid ?? true);

    React.useLayoutEffect(() => {
      setDefaultResourceName(result.resourceName?.defaultValue ?? "");
    }, [result.resourceName?.defaultValue]);

    const resourceNameValidation = useAsyncValidation({
      value: debouncedResourceName,
      validation: customResourceNameValidation,
      preflightCheck: (value) => value.length >= 3 && isResourceNameValid(),
    });

    const asyncError = React.useMemo(() => {
      if (resourceNameValidation.message !== undefined) {
        return { message: resourceNameValidation.message };
      }
    }, [resourceNameValidation.message]);

    const maybeResourceName = React.useMemo(() => {
      return maybe.map(result.resourceName, () => ({
        name: htmlInputNames.resourceName ?? "resourceName",
        value: resourceName,
        validating: resourceNameValidation.validating,
        minLength: minResourceNameLength,
        maxLength: maxResourceNameLength,
        pattern: resourceNamePattern,
        patternMismatchMessage: resourcePatternMismatchMessage,
        onChange: (str: string) => {
          batch(() => {
            resourceNameValidation.reset();
            setResourceName(str);
          });
        },
        error: asyncError,
        ref: resourceNameRef,
      }));
    }, [
      resourceName,
      resourceNameValidation.validating,
      minResourceNameLength,
      maxResourceNameLength,
      resourceNamePattern,
      resourcePatternMismatchMessage,
      asyncError,
      htmlInputNames.resourceName,
    ]);

    const maybeTags = React.useMemo(() => {
      return maybe.map(
        result.tags,
        (tags): TagsState => ({
          name: htmlInputNames.tags ?? "tags",
          search: tagSearch,
          onChangeSearch: setTagSearch,
          selected: selectedTags,
          onSelectMany: setSelectedTags,

          data: helpers.async.map(tags, (next) => ({
            options: next.options.map((option) => ({
              ...option,
              group: next.type === "suggested" ? "Suggested tags" : "Matched tags",
            })),
            searching: next.searching,
          })),
        })
      );
    }, [tagSearch, selectedTags, result.tags, htmlInputNames.tags]);

    const asyncProjects = result.projects ?? readyNothing;

    const projects = React.useMemo(() => {
      return helpers.async.map(asyncProjects, (next) =>
        transformDataToMaybeEntityFormState(next, {
          currentValue: project,
          debugKey: "projects",
          extra: {
            name: htmlInputNames.project ?? "project",
            onSelect: setProject,
          },
        })
      );
    }, [project, asyncProjects, htmlInputNames.project]);

    return React.useMemo(() => {
      return {
        resourceName: maybeResourceName,
        tags: maybeTags,
        projects,
        values: {
          resourceName,
          project: asyncData.is.ready(projects) && maybe.is.some(projects.data) ? projects.data.value.selected : null,
          tags: selectedTags.map((tag) => tag.value),
        },
        valid: isResourceNameValid() && asyncError === undefined && asyncData.is.ready(projects),
      };
    }, [maybeResourceName, maybeTags, projects, resourceName, selectedTags]);
  };
}
